import React from 'react';

import { Label } from '@/components/ui/label';
import { cn } from '@/utils';

interface ProviderToggleProps<T extends string | number> {
  options: { value: T; label: string }[];
  /** Nothing is checked while undefined. */
  value: T | undefined;
  /** Locked once a call is up: the carrier is fixed at dial time. */
  disabled?: boolean;
  onChange: (value: T) => void;
}

/** The dialpad's carrier choice: "Provider" and a pill toggle on one line. */
export function ProviderToggle<T extends string | number>({
  options,
  value,
  disabled = false,
  onChange,
}: ProviderToggleProps<T>) {
  return (
    <div className="mb-3 flex items-center justify-between gap-3">
      <Label className="text-xs font-medium text-muted-foreground">Provider</Label>
      <div
        role="radiogroup"
        aria-label="Provider"
        className="inline-flex rounded-full border border-border bg-muted p-0.5"
      >
        {options.map(option => {
          const checked = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={checked}
              disabled={disabled}
              onClick={() => onChange(option.value)}
              className={cn(
                'whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
                'disabled:cursor-not-allowed',
                checked
                  ? 'bg-primary font-semibold text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground disabled:opacity-50',
              )}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
